import api from "./api";

/**
 * demographics.js
 *
 * Thin wrapper over the existing `api` client (lib/api.js), so these
 * calls inherit the same auth-header injection, encryption interceptor,
 * and 401 handling as the rest of the app — no parallel client.
 *
 * Routes proxy through the gateway to the Auth service (3002), per the
 * decision to keep demographics adjacent to platform_user identity.
 */

export const createDraft = (payload) =>
  api.post("/demographics", payload).then((r) => r.data);

export const updateDraft = (id, payload) =>
  api.patch(`/demographics/${id}`, payload).then((r) => r.data);

export const submitDraft = (id) =>
  api.post(`/demographics/${id}/submit`).then((r) => r.data);

export const fetchMyDemographicStatus = () =>
  api.get("/demographics/me").then((r) => r.data);

export const listSubmissions = (params) =>
  api.get("/demographics", { params }).then((r) => r.data);

export const getSubmission = (id) =>
  api.get(`/demographics/${id}`).then((r) => r.data);

export const getSubmissionHistory = (id) =>
  api.get(`/demographics/${id}/history`).then((r) => r.data);

export const verifySubmission = (id) =>
  api.post(`/demographics/${id}/verify`).then((r) => r.data);

export const rejectSubmission = (id, rejection_reason) =>
  api
    .post(`/demographics/${id}/reject`, { rejection_reason })
    .then((r) => r.data);

export const supersedeSubmission = (id) =>
  api.post(`/demographics/${id}/supersede`).then((r) => r.data);

export const submitOnBehalfOf = async (payload) => {
  const response = await api.post("/demographics/on-behalf-of", payload);
  return response.data;
};

export const submitAdminCorrection = async (patientId, payload) => {
  const response = await api.post(
    `/demographics/patient/${patientId}/correction`,
    payload,
  );
  return response.data;
};

// Backs the on-behalf-of target picker. Hits the widened /users route
// (admin + clinician, tenant-scoped server-side). Returns
// platform_user_id, display_name, email, and patient_id (null if the
// target has no canonical patient record yet — most on-behalf-of
// targets won't).
export const searchPatients = (q) =>
  api.get("/users", { params: { role: "patient", q } }).then((r) => r.data);

// Add alongside the other exports in this file (whatever its real
// filename turns out to be — see import-path flag above).
// Matches submitAdminCorrection's style: thin wrapper over `api`,
// same auth/encryption/401 handling inherited automatically.
export const getPatientForCorrection = (patientId) =>
  api.get(`/demographics/patient/${patientId}`).then((r) => r.data);

// Backs PatientList.jsx and PatientRoster.jsx. Full-tenant patient
// listing via the governed /users route (tenant-scoped server-side).
// Returns platform_user_id, display_name, email, patient_id (null
// until Stage 2 canonical linkage exists for that user), and
// patient_updated_at. Does NOT return thresholds/clinical fields —
// those live on the legacy `users` row and have no canonical-side
// equivalent yet (see: Patient Roster Risk Badge, on hold pending
// canonical patient linkage validation).
export const listPatients = (params) =>
  api.get("/users", { params: { role: "patient", ...params } }).then((r) => r.data);